import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../Components/common/Navbar";
import Footer from "../../Components/common/Footer";
import { message } from "antd";
import { courseService } from "../../api/course.service";
import { learningService } from "../../api/learning.service";
import { API_BASE_URL } from "../../api/constant";
import {
  faGraduationCap,
  faAward,
  faStar,
  faUsers,
  faBook,
  faTrophy,
  faPlay,
  faArrowRight,
  faChevronDown,
  faCheckCircle,
  faDesktop,
  faCode,
  faBriefcase,
  faDatabase,
  faGlobe,
  faQuoteLeft,
  faEnvelope,
  faRocket,
  faBolt,
  faShieldAlt,
  faUser,
  faFire,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import c1 from "../../assets/images/c1.jpg";
import c2 from "../../assets/images/html.png";
import c3 from "../../assets/images/sql.jpg";
import c4 from "../../assets/images/python.jpg";
import c5 from "../../assets/images/java.png";
import c6 from "../../assets/images/css.png";

function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function AnimatedCounter({ target, duration = 2000 }) {
  const [count, setCount] = useState(0);
  const [ref, inView] = useInView(0.2);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [inView, target, duration]);
  return <span ref={ref}>{count.toLocaleString()}</span>;
}

export default function Home() {
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [heroVisible, setHeroVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);

  const [courses, setCourses] = useState([]);
  const [enrolled, setEnrolled] = useState([]);
  const [enrollingId, setEnrollingId] = useState(null);

  const userId = localStorage.getItem("id");
  const authToken = localStorage.getItem("token");

  const [featuresRef, featuresInView] = useInView();
  const [coursesRef, coursesInView] = useInView();
  const [catsRef, catsInView] = useInView();
  const [stepsRef, stepsInView] = useInView();
  const [testiRef, testiInView] = useInView();

  useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const targetDate = new Date();
    targetDate.setDate(targetDate.getDate() + 2);
    const tick = () => {
      const dist = targetDate.getTime() - Date.now();
      if (dist <= 0) { setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 }); return; }
      setTimeLeft({
        days: Math.floor(dist / 86400000),
        hours: Math.floor((dist / 3600000) % 24),
        minutes: Math.floor((dist / 60000) % 60),
        seconds: Math.floor((dist / 1000) % 60),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const coursesRes = await courseService.getAllCourses();
        if (coursesRes.success && Array.isArray(coursesRes.data)) {
          setCourses(coursesRes.data.slice(0, 6)); // Top 6 courses
        }

        if (userId) {
          const enrollmentsRes = await learningService.getEnrollments(userId);
          if (enrollmentsRes.success && Array.isArray(enrollmentsRes.data)) {
            setEnrolled(enrollmentsRes.data.map((item) => item.course_id));
          }
        }
      } catch (err) {
        console.error("Error loading courses:", err);
      }
    };

    fetchData();
  }, [userId]);

  const enrollCourse = async (courseId) => {
    if (!authToken) {
      message.error("You need to login to enroll");
      setTimeout(() => navigate("/login"), 1500);
      return;
    }

    setEnrollingId(courseId);
    try {
      const res = await learningService.enrollCourse(userId, courseId);

      if (res.success) {
        if (res.data === "Enrolled successfully") {
          setEnrolled((prev) => [...prev, courseId]);
          message.success("Enrolled successfully!");
          setTimeout(() => navigate(`/course/${courseId}`), 1500);
        } else if (res.data === "Course already enrolled") {
          message.info("You are already enrolled in this course");
          navigate(`/course/${courseId}`);
        } else {
          message.error(res.data || "Failed to enroll. Please try again.");
        }
      } else {
        message.error("Enrollment failed. Please try again.");
      }
    } catch (err) {
      message.error("Something went wrong. Please try again.");
    } finally {
      setEnrollingId(null);
    }
  };

  const features = [
    {
      icon: faGraduationCap,
      title: "Scholarship Facility",
      desc: "Access world-class education with financial support programs designed to empower every learner.",
      gradient: "from-primary to-primary-dark",
    },
    {
      icon: faBolt,
      title: "Learn at Your Pace",
      desc: "Flexible schedules, lifetime access, and bite-sized lessons let you learn whenever and wherever.",
      gradient: "from-warning to-yellow-500",
    },
    {
      icon: faAward,
      title: "Global Certification",
      desc: "Earn recognized certificates that validate your skills and open doors to global opportunities.",
      gradient: "from-secondary to-secondary-dark",
    },
    {
      icon: faRocket,
      title: "Career Acceleration",
      desc: "Job-ready curriculum built with industry leaders to fast-track your professional growth.",
      gradient: "from-purple-500 to-indigo-600",
    },
    {
      icon: faShieldAlt,
      title: "Quality Guaranteed",
      desc: "Every course is reviewed and updated regularly so you always get accurate, relevant content.",
      gradient: "from-teal-500 to-primary",
    },
    {
      icon: faUsers,
      title: "Community Support",
      desc: "Join a global community of learners, share insights, and get answers from peers and mentors.",
      gradient: "from-pink-500 to-rose-500",
    },
  ];

  const stats = [
    { icon: faUsers, value: 10000, label: "Students Enrolled", suffix: "+" },
    { icon: faBook, value: 100, label: "Expert Courses", suffix: "+" },
    { icon: faAward, value: 95, label: "Success Rate", suffix: "%" },
    { icon: faTrophy, value: 50, label: "Top Instructors", suffix: "+" },
  ];

  const categories = [
    { icon: faCode, title: "Web Development", desc: "Build modern, responsive websites and web apps with the latest technologies." },
    { icon: faBriefcase, title: "Business & Leadership", desc: "Master essential business skills and lead teams to achieve remarkable results." },
    { icon: faDatabase, title: "Data Science", desc: "Turn raw data into meaningful insights and drive smarter business decisions." },
  ];

  const steps = [
    { step: "01", title: "Create Account", desc: "Sign up for free in seconds and get immediate access to our full course catalog." },
    { step: "02", title: "Choose a Course", desc: "Browse hundreds of courses and pick the one that matches your learning goals." },
    { step: "03", title: "Start Learning", desc: "Learn at your own pace with video lessons, quizzes, and hands-on projects." },
  ];

  const testimonials = [
    {
      name: "Ahmed Khan",
      role: "Frontend Developer",
      text: "Alkauser Academy completely transformed my career. The courses are practical, well-structured, and the instructors are absolutely world-class.",
      rating: 5,
      initials: "AK",
      from: "from-blue-400",
      to: "to-indigo-500",
    },
    {
      name: "Sara Malik",
      role: "Data Analyst",
      text: "I landed my first data science job after completing the Python and SQL courses. The certification genuinely made a difference to recruiters.",
      rating: 5,
      initials: "SM",
      from: "from-primary",
      to: "to-teal-400",
    },
    {
      name: "Usman Ali",
      role: "UI/UX Designer",
      text: "The best online learning platform I've used. Content is always up to date, and the structured learning path is super clear.",
      rating: 5,
      initials: "UA",
      from: "from-secondary",
      to: "to-orange-400",
    },
  ];

  return (
    <div className="bg-gray-50 min-h-screen overflow-x-hidden">
      <Navbar page="home" />

      {/* ══════════════════════════════════════
          HERO
      ══════════════════════════════════════ */}
      <section className="relative text-center h-screen overflow-hidden flex items-center justify-center bg-gradient-to-br from-[#0a2e2d] via-primary-dark to-primary">

        {/* dot-grid texture */}
        <div
          className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle, #ffffff 1px, transparent 1px)", backgroundSize: "28px 28px" }}
        />

        {/* large orange arc — top right */}
        <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full border-[40px] border-secondary/25 pointer-events-none" />
        <div className="absolute -top-8 -right-8 w-[260px] h-[260px] rounded-full border-[20px] border-warning/15 pointer-events-none" />

        {/* bottom-left glow blobs */}
        <div className="absolute -bottom-16 -left-16 w-80 h-80 bg-secondary/20 rounded-full blur-3xl animate-float pointer-events-none" />
        <div className="absolute bottom-1/3 left-8 w-48 h-48 bg-warning/10 rounded-full blur-2xl animate-float-slow pointer-events-none" />

        {/* mid-right subtle circle */}
        <div className="absolute top-1/2 -right-12 w-56 h-56 bg-primary-light/10 rounded-full blur-2xl animate-pulse-slow pointer-events-none" />

        <div
          className={`relative z-10 px-6 max-w-4xl mx-auto transition-all duration-1000 ease-out ${
            heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
          }`}
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-5 py-2 mb-8 text-white/90 text-sm font-medium">
            <span className="w-2 h-2 bg-warning rounded-full animate-ping" />
            Join 10,000+ learners today
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold leading-tight mb-6 text-white">
            Enhance Your Future
            <span className="block mt-3 bg-gradient-to-r from-warning via-yellow-300 to-secondary bg-clip-text text-transparent">
              with Alkauser Academy 
            </span>
          </h1>

          <p className="text-gray-200 max-w-2xl mx-auto text-lg md:text-xl mb-10 leading-relaxed">
            Unlock your potential with hundreds of expert-led courses, earn global certifications,
            and build the career you've always wanted.
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={() => navigate("/courses")}
              className="group px-9 py-4 rounded-2xl bg-warning text-black font-bold text-base hover:bg-yellow-300 transition-all duration-300 shadow-lg shadow-warning/30 hover:shadow-xl hover:scale-105 flex items-center justify-center gap-2"
            >
              Explore Courses
              <FontAwesomeIcon icon={faArrowRight} className="group-hover:translate-x-1 transition-transform duration-300" />
            </button>
            <a
              href="#features"
              className="px-9 py-4 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/30 text-white font-bold text-base hover:bg-white/20 transition-all duration-300 flex items-center justify-center gap-2 hover:scale-105"
            >
              <FontAwesomeIcon icon={faPlay} className="text-warning text-sm" />
              Learn More
            </a>
          </div>
        </div>

        <a href="#stats" className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60 hover:text-white transition-colors duration-300 animate-bounce">
          <FontAwesomeIcon icon={faChevronDown} className="text-2xl" />
        </a>
      </section>

      {/* ══════════════════════════════════════
          STATS BAR
      ══════════════════════════════════════ */}
      <section id="stats" className="bg-white shadow-md py-14 px-6">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {stats.map((stat, i) => (
            <div key={i} className="group">
              <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-primary-light flex items-center justify-center group-hover:bg-primary transition-colors duration-300">
                <FontAwesomeIcon icon={stat.icon} className="text-primary group-hover:text-white transition-colors duration-300 text-xl" />
              </div>
              <p className="text-3xl font-extrabold text-gray-800">
                <AnimatedCounter target={stat.value} />{stat.suffix}
              </p>
              <p className="text-gray-500 text-sm mt-1">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════════
          FEATURES — 6-card grid
      ══════════════════════════════════════ */}
      <section id="features" className="px-6 py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-primary font-semibold text-sm uppercase tracking-widest">Why Choose Us</span>
            <h2 className="text-4xl font-extrabold mt-2 text-gray-800">Everything You Need</h2>
            <p className="text-gray-500 mt-3 max-w-xl mx-auto">Six reasons thousands of learners choose Alkauser Academy to level up.</p>
          </div>

          <div ref={featuresRef} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
            {features.map((f, i) => (
              <div
                key={i}
                className={`group p-7 rounded-2xl bg-white shadow-sm hover:shadow-xl border border-gray-100 hover:border-primary/20 transition-all duration-500 hover:-translate-y-2 ${
                  featuresInView ? "animate-fadeInUp" : "opacity-0"
                }`}
                style={{ animationDelay: `${i * 100}ms`, animationFillMode: "both" }}
              >
                <div className={`mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br ${f.gradient} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300 shadow-md`}>
                  <FontAwesomeIcon icon={f.icon} className="text-white text-xl" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-800 group-hover:text-primary transition-colors duration-300">{f.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          POPULAR COURSES
      ══════════════════════════════════════ */}
        <section id="courses" className="px-6 py-24" style={{ backgroundColor: "#f5f0e8" }}>
          <div className="max-w-7xl mx-auto">

            {/* Section header */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-14">
              <div>
                <span className="text-secondary font-semibold text-sm uppercase tracking-widest">
                  Top Picks
                </span>
                <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mt-2 leading-tight">
                  Popular Courses
                </h2>
                <p className="text-gray-500 mt-3 max-w-md">
                  Hand-picked by our experts — start learning today for free.
                </p>
              </div>
              <button
                onClick={() => navigate("/courses")}
                className="shrink-0 flex items-center gap-2 px-6 py-3 rounded-full border-2 border-primary text-primary font-bold hover:bg-primary hover:text-white transition-all duration-300 hover:scale-105 self-start md:self-auto"
              >
                View All Courses
                <FontAwesomeIcon icon={faArrowRight} className="text-sm" />
              </button>
            </div>

            {/* Cards grid */}
            <div ref={coursesRef} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {courses.map((course, i) => {
                const isEnrolled = enrolled.includes(course.course_id);
                const isEnrolling = enrollingId === course.course_id;
                const isFree = !course.price || Number(course.price) === 0;

                return (
                <div
                  key={course.course_id}
                  onClick={() => isEnrolled ? navigate(`/course/${course.course_id}`) : enrollCourse(course.course_id)}
                  className={`group bg-white rounded-3xl overflow-hidden cursor-pointer border border-gray-100
                    shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-3
                    ${coursesInView ? "animate-fadeInUp" : "opacity-0"}`}
                  style={{ animationDelay: `${i * 110}ms`, animationFillMode: "both" }}
                >
                  {/* ── Image area ── */}
                  <div className="relative overflow-hidden h-52">
                    <img
                      src={course.hasImage
                        ? `${API_BASE_URL}/api/courses/${course.course_id}/course-image`
                        : "https://placehold.co/600x400/1d7270/ffffff?text=Course"}
                      alt={course.course_name}
                      onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/600x400/1d7270/ffffff?text=Course"; }}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    {/* dark-to-transparent gradient so bottom badges are readable */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

                    {/* category — top left */}
                    <div className="absolute top-4 left-4">
                      <span className="bg-primary text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg">
                        Course
                      </span>
                    </div>

                    {/* FREE / Price — top right */}
                    <div className="absolute top-4 right-4">
                      <span className="bg-warning text-black text-xs font-extrabold px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1">
                        <FontAwesomeIcon icon={faFire} className="text-orange-500 text-[10px]" />
                        {isFree ? "FREE" : `₹${course.price}`}
                      </span>
                    </div>

                    {/* Enrolled badge — bottom left over image */}
                    {isEnrolled && (
                      <div className="absolute bottom-4 left-5 flex items-center gap-1.5 bg-primary text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg">
                        <FontAwesomeIcon icon={faCheckCircle} className="text-[10px]" />
                        <span>Enrolled</span>
                      </div>
                    )}
                  </div>

                  {/* ── Card body ── */}
                  <div className="p-6">
                    {/* Stars + reviews */}
                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(5)].map((_, s) => (
                        <FontAwesomeIcon key={s} icon={faStar} className="text-warning text-xs" />
                      ))}
                      <span className="text-gray-400 text-xs ml-2 font-medium">(4.9 reviews)</span>
                    </div>

                    {/* Title */}
                    <h3 className="font-extrabold text-start text-gray-900 text-lg leading-snug mb-3 group-hover:text-primary transition-colors duration-300 line-clamp-2">
                      {course.course_name?.length < 8
                        ? `${course.course_name} Tutorial`
                        : course.course_name}
                    </h3>

                    {/* Instructor */}
                    <div className="flex items-center gap-2 mb-5">
                      <div className="w-7 h-7 rounded-full bg-primary-light flex items-center justify-center shrink-0">
                        <FontAwesomeIcon icon={faUser} className="text-primary text-xs" />
                      </div>
                      <span className="text-gray-500 text-sm font-medium">{course.instructor}</span>
                    </div>

                    {/* Divider */}
                    <div className="border-t border-gray-100 pt-4 flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-gray-400 text-xs font-medium">
                        <FontAwesomeIcon icon={faUsers} className="text-gray-300" />
                        Students enrolled
                      </div>
                      {isEnrolled ? (
                        <button 
                          onClick={(e) => { e.stopPropagation(); navigate(`/course/${course.course_id}`); }}
                          className="flex items-center gap-2 bg-primary-light text-primary border border-primary/20 rounded-full px-4 py-2.5 text-xs font-bold hover:bg-primary hover:text-white transition-all duration-300 group-hover:scale-105"
                        >
                          <FontAwesomeIcon icon={faPlay} className="text-[10px]" />
                          Continue
                        </button>
                      ) : (
                        <button
                          onClick={(e) => { e.stopPropagation(); enrollCourse(course.course_id); }}
                          disabled={isEnrolling}
                          className="flex items-center gap-2 bg-primary text-white rounded-full px-5 py-2.5 text-sm font-bold hover:bg-primary-dark transition-all duration-300 shadow-sm group-hover:shadow-lg group-hover:shadow-primary/30 group-hover:scale-105 disabled:opacity-60 disabled:cursor-not-allowed disabled:scale-100"
                        >
                          {isEnrolling ? (
                            <>
                              <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              Enrolling...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faPlay} className="text-[10px]" />
                              Start Now
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════
            COURSE CATEGORIES
        ══════════════════════════════════════ */}
  <section className="px-6 py-24 bg-white overflow-hidden">
    <div className="max-w-6xl mx-auto relative">
      
      {/* TOP GRID */}
      <div className="gap-12 items-center mb-20 text-">
        
        {/* LEFT CONTENT */}
        <div>
          <span className="text-secondary font-semibold text-sm uppercase tracking-widest mb-3 block">
            Course Categories
          </span>

          <h2 className="text-5xl md:text-6xl font-extrabold text-gray-900 leading-tight mb-6">
            Courses that <br /> change your life!
          </h2>

          <p className="text-gray-500 leading-relaxed mb-8">
            We offer expert-curated courses across technology, business, design,
            and science — all crafted to help you grow at your own pace.
          </p>
        </div>
      </div>

      {/* BOTTOM SECTION */}
      <div className="bg-secondary rounded-2xl grid md:grid-cols-3 overflow-hidden shadow-xl">
        
        {categories.map((cat, i) => (
          <div
            key={i}
            className={`p-10 transition-all duration-300 hover:brightness-95 ${
              i === 0
                ? "bg-white rounded-xl shadow-md m-4"
                : "text-white border-l border-white/20"
            }`}
          >
            <div
              className={`w-14 h-14 rounded-full flex items-center justify-center mb-6 shadow-md ${
                i === 0 ? "bg-secondary" : "bg-white/20"
              }`}
            >
              <FontAwesomeIcon icon={cat.icon} className="text-white text-xl" />
            </div>

            <h3
              className={`font-bold text-xl mb-3 ${
                i === 0 ? "text-gray-900" : "text-white"
              }`}
            >
              {cat.title}
            </h3>

            <p
              className={`text-sm mb-6 leading-relaxed ${
                i === 0 ? "text-gray-500" : "text-white/80"
              }`}
            >
              {cat.desc}
            </p>

            <button
              onClick={() => navigate("/courses")}
              className={`text-sm font-semibold underline underline-offset-4 hover:opacity-70 ${
                i === 0 ? "text-secondary" : "text-white"
              }`}
            >
              Learn more
            </button>
          </div>
        ))}
      </div>
    </div>
  </section>
      {/* ══════════════════════════════════════
          HOW IT WORKS
      ══════════════════════════════════════ */}
      <section className="px-6 py-20 bg-primary-light">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-primary font-semibold text-sm uppercase tracking-widest">Simple Process</span>
            <h2 className="text-4xl font-extrabold mt-2 text-gray-800">How It Works</h2>
            <p className="text-gray-500 mt-3">Get started with Alkauser Academy in just 3 easy steps.</p>
          </div>
          <div ref={stepsRef} className="grid md:grid-cols-3 gap-10 relative">
            <div className="hidden md:block absolute top-10 left-[calc(16.66%+2rem)] right-[calc(16.66%+2rem)] h-0.5 bg-primary/20 z-0" />
            {steps.map((step, i) => (
              <div
                key={i}
                className={`relative z-10 text-center transition-all duration-700 ${stepsInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
                style={{ transitionDelay: `${i * 200}ms` }}
              >
                <div className="w-20 h-20 rounded-full bg-primary text-white text-2xl font-extrabold flex items-center justify-center mx-auto mb-5 shadow-lg shadow-primary/30 hover:scale-110 transition-transform duration-300 ring-4 ring-primary/20">
                  {step.step}
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-800">{step.title}</h3>
                <p className="text-gray-500 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          TESTIMONIALS
      ══════════════════════════════════════ */}
      <section className="px-6 py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-secondary font-semibold text-sm uppercase tracking-widest">Student Stories</span>
            <h2 className="text-4xl font-extrabold mt-2 text-gray-800">What Our Learners Say</h2>
            <p className="text-gray-500 mt-3 max-w-xl mx-auto">Real results from real students who transformed their careers with EduVerse.</p>
          </div>

          <div ref={testiRef} className="grid md:grid-cols-3 gap-7">
            {testimonials.map((t, i) => (
              <div
                key={i}
                className={`bg-white rounded-2xl p-7 shadow-sm hover:shadow-xl border border-gray-100 hover:border-primary/20 transition-all duration-500 hover:-translate-y-2 flex flex-col ${
                  testiInView ? "animate-fadeInUp" : "opacity-0" 
                }`}
                style={{ animationDelay: `${i * 150}ms`, animationFillMode: "both" }}
              >
                {/* quote icon */}
                <div className="w-10 h-10 rounded-xl bg-primary-light flex items-center justify-center mb-5">
                  <FontAwesomeIcon icon={faQuoteLeft} className="text-primary text-base" />
                </div>

                {/* stars */}
                <div className="flex gap-0.5 mb-4">
                  {[...Array(t.rating)].map((_, s) => (
                    <FontAwesomeIcon key={s} icon={faStar} className="text-warning text-sm" />
                  ))}
                </div>

                {/* review text */}
                <p className="text-gray-600 leading-relaxed text-sm flex-1 mb-6">"{t.text}"</p>

                {/* avatar + name */}
                <div className="flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-full bg-gradient-to-br ${t.from} ${t.to} flex items-center justify-center text-white font-bold text-sm shrink-0`}>
                    {t.initials}
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-sm">{t.name}</p>
                    <p className="text-gray-400 text-xs">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          CTA + COUNTDOWN
      ══════════════════════════════════════ */}
      <section className="relative px-6 py-24 overflow-hidden bg-gradient-to-br from-primary-dark via-primary to-secondary/80">
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-warning/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-16 -left-16 w-72 h-72 bg-white/5 rounded-full blur-2xl pointer-events-none" />

        <div className="relative z-10 max-w-3xl mx-auto text-center text-white">
          <span className="inline-flex items-center gap-2 bg-warning/20 border border-warning/40 rounded-full px-4 py-2 text-warning font-semibold text-sm mb-6">
            <FontAwesomeIcon icon={faTrophy} /> Limited Time Offer
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold mb-5 leading-tight">
            Get 100 Online Courses <br className="hidden md:block" /> for Free!
          </h2>
          <p className="text-white/80 text-lg mb-10 max-w-xl mx-auto">
            Join thousands of learners and access our top courses for free. Earn certifications and grow your career.
          </p>

          <div className="flex flex-wrap gap-4 justify-center mb-10">
            {[
              { label: "Days", value: timeLeft.days },
              { label: "Hours", value: timeLeft.hours },
              { label: "Minutes", value: timeLeft.minutes },
              { label: "Seconds", value: timeLeft.seconds },
            ].map((item, i) => (
              <div key={i} className="flex flex-col items-center justify-center bg-white/10 backdrop-blur-md border border-white/20 w-24 h-24 md:w-28 md:h-28 rounded-2xl shadow-xl hover:bg-white/20 transition-all duration-300">
                <span className="text-3xl md:text-4xl font-extrabold text-warning">
                  {String(item.value).padStart(2, "0")}
                </span>
                <span className="text-white/70 text-xs font-semibold mt-1 uppercase tracking-wider">{item.label}</span>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={() => navigate("/register")}
              className="group px-10 py-4 rounded-2xl bg-warning text-black font-bold text-base hover:bg-yellow-300 transition-all duration-300 shadow-lg shadow-warning/30 hover:scale-105 inline-flex items-center gap-3"
            >
              Register for Free
              <FontAwesomeIcon icon={faArrowRight} className="group-hover:translate-x-1 transition-transform duration-300" />
            </button>
            <div className="flex items-center gap-2 text-white/70 text-sm">
              <FontAwesomeIcon icon={faCheckCircle} className="text-warning" />
              No credit card required
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          NEWSLETTER
      ══════════════════════════════════════ */}
      <section className="px-6 py-16 bg-white">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-14 h-14 mx-auto mb-5 rounded-2xl bg-primary-light flex items-center justify-center">
            <FontAwesomeIcon icon={faEnvelope} className="text-primary text-2xl" />
          </div>
          <h2 className="text-3xl font-extrabold text-gray-900 mb-3">Stay in the Loop</h2>
          <p className="text-gray-500 mb-8">
            Get updates on new courses, exclusive offers, and learning resources delivered straight to your inbox.
          </p>

          {subscribed ? (
            <div className="flex items-center justify-center gap-3 text-primary font-semibold text-lg py-4">
              <FontAwesomeIcon icon={faCheckCircle} className="text-2xl" />
              You're subscribed! Welcome aboard.
            </div>
          ) : (
            <form
              onSubmit={(e) => { e.preventDefault(); if (email.trim()) setSubscribed(true); }}
              className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
            >
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                required
                className="flex-1 px-5 py-3 rounded-xl border border-gray-200 text-gray-800 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
              />
              <button
                type="submit"
                className="px-7 py-3 bg-primary text-white font-bold rounded-xl hover:bg-primary-dark transition-all duration-300 hover:scale-105 shadow-md shadow-primary/20 whitespace-nowrap"
              >
                Subscribe
              </button>
            </form>
          )}

          <p className="text-gray-400 text-xs mt-4">No spam, ever. Unsubscribe at any time.</p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
